import type { TFunction } from "i18next";
import { SUPER_TENANT_ONLY_ACTION_ROUTES } from "@nexus/contracts";
import { ApiError } from "@/lib/api-client";
import { iamQueryErrorMessage } from "@/features/identity-hub/lib/iam-query-error";
import { amberFailureToastMessage } from "@/lib/use-amber-mutation";

const CANNOT_REVOKE = /Cannot revoke permission/i;
const CANNOT_GRANT = /Cannot grant permission/i;
const REVOKE_ROUTE = /Cannot revoke permission \[[^\]]+\] \S+ (\S+)/i;

const CANNOT_REVOKE_PLATFORM_COPY =
  "You can't remove platform-only actions from this role. Create a new role without those actions, or ask a platform admin to edit it.";
const CANNOT_REVOKE_UNGRANTABLE_COPY =
  "You can't remove this action because you don't have it yourself. Create a new role without it, or ask a platform admin.";
const CANNOT_GRANT_COPY =
  "You can't add permissions you don't have yourself. Grant only actions you hold, or ask a platform admin.";

function revokedRouteIsPlatformOnly(message: string): boolean {
  const route = REVOKE_ROUTE.exec(message)?.[1];
  return Boolean(route && SUPER_TENANT_ONLY_ACTION_ROUTES.has(route));
}

/**
 * Identity Hub role save/preview is operator-facing: 403 grantable errors and
 * 400 page-deps must not collapse into the generic customer toast.
 * 401 stays silent so the session handler owns that UX.
 */
export function resolveTemplateSaveErrorMessage(
  error: unknown,
  t: TFunction,
  fallback: string,
): string | null {
  if (error instanceof ApiError && error.status === 401) return null;

  if (error instanceof ApiError && error.status === 403) {
    if (CANNOT_REVOKE.test(error.message)) {
      if (revokedRouteIsPlatformOnly(error.message)) {
        return t("iam.toast.cannotRevokePermission", CANNOT_REVOKE_PLATFORM_COPY);
      }
      return t("iam.toast.cannotRevokeUngrantable", CANNOT_REVOKE_UNGRANTABLE_COPY);
    }
    if (CANNOT_GRANT.test(error.message)) {
      return t("iam.toast.cannotGrantPermission", CANNOT_GRANT_COPY);
    }
  }

  if (error instanceof Error && error.name === "AmberWorkerFailureError") {
    return amberFailureToastMessage(error, true) ?? fallback;
  }

  if (error instanceof ApiError && error.status >= 400 && error.status < 500) {
    return iamQueryErrorMessage(error, fallback);
  }

  return fallback;
}
